import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  InputAdornment,
  Typography,
  Box,
} from "@mui/material";
import { useSnackbar } from "notistack";
import driverApi from "../apis/driverApi";
import donationApi from "../apis/donationApi";

interface Props {
  open: boolean;
  handleClose: () => void;
  refresh: () => void;
  orderId: string;
  order?: any;
}

const ModalAssignDriver = ({ open, handleClose, refresh, orderId, order }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<string>("");
  const [rateType, setRateType] = useState<"fixed" | "percentage">("fixed");
  const [customValue, setCustomValue] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const getPartnerId = (): string => {
    return process.env.REACT_APP_ENV === "production" ? "01G486CQAV7VW2FK3DHZV1T2FG" : "01G4866BBJGYR1JH8E6RPYEW2C";
  };

  const basePrice = order?.shippingPrice || order?.totalPrice || 0;

  useEffect(() => {
    if (open) {
      // Set existing driver if available
      if (order?.driverId) {
        setSelectedDriver(order.driverId);
      } else {
        setSelectedDriver("");
      }

      // Check if driver income config was already saved locally for this order or returned from backend
      try {
        const storedConfigs = JSON.parse(localStorage.getItem("driver_order_incomes") || "{}");
        const existing = storedConfigs[orderId];
        if (existing) {
          setRateType(existing.type || "fixed");
          setCustomValue(existing.value?.toString() || "");
        } else if (order?.driverIncome !== undefined && order?.driverIncome !== null && Number(order.driverIncome) > 0) {
          setRateType("fixed");
          setCustomValue(order.driverIncome.toString());
        } else {
          setRateType("fixed");
          setCustomValue("");
        }
      } catch (e) {
        if (order?.driverIncome !== undefined && order?.driverIncome !== null && Number(order.driverIncome) > 0) {
          setRateType("fixed");
          setCustomValue(order.driverIncome.toString());
        } else {
          setRateType("fixed");
          setCustomValue("");
        }
      }

      driverApi.listDrivers(getPartnerId()).then((res: any) => {
        if (res.data.code === 0) {
          setDrivers(res.data.data || []);
        }
      }).catch((err: any) => console.log(err));
    }
  }, [open, orderId, order]);

  // Calculate preview of earnings
  const calculateEstimatedEarnings = (): number => {
    if (customValue.trim() === "") {
      return 0;
    }
    const val = parseFloat(customValue) || 0;
    if (rateType === "fixed") {
      return Math.max(0, val);
    } else {
      // Percentage of base price
      return Math.max(0, Math.round((basePrice * val) / 100));
    }
  };

  const handleSubmit = async () => {
    if (!selectedDriver) {
      enqueueSnackbar("Vui lòng chọn tài xế", { variant: "warning" });
      return;
    }

    setLoading(true);
    try {
      const calculatedIncome = calculateEstimatedEarnings();

      // Save income configuration locally so driver portal and admin list can reflect exact configured earnings
      try {
        const storedConfigs = JSON.parse(localStorage.getItem("driver_order_incomes") || "{}");
        storedConfigs[orderId] = {
          driverId: selectedDriver,
          type: rateType,
          value: customValue.trim() === "" ? 0 : (parseFloat(customValue) || 0),
          calculatedIncome: calculatedIncome,
          configuredAt: new Date().toISOString(),
        };
        localStorage.setItem("driver_order_incomes", JSON.stringify(storedConfigs));
      } catch (e) {
        console.error("Failed to save driver income config locally", e);
      }

      const res = await donationApi.assignDriver({
        orderId,
        driverId: selectedDriver,
        driverIncome: calculatedIncome,
      });

      if (res.data.code === 0) {
        enqueueSnackbar("Gán tài xế và cấu hình thù lao thành công!", { variant: "success" });
        refresh();
        handleClose();
      } else {
        enqueueSnackbar(res.data.message || "Gán thất bại", { variant: "error" });
      }
    } catch (err: any) {
      console.error(err);
      enqueueSnackbar(err.response?.data?.message || "Lỗi khi gán tài xế", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    if (!window.confirm("Bạn có chắc chắn muốn gỡ tài xế khỏi đơn hàng này?")) {
      return;
    }
    setLoading(true);
    try {
      try {
        const storedConfigs = JSON.parse(localStorage.getItem("driver_order_incomes") || "{}");
        delete storedConfigs[orderId];
        localStorage.setItem("driver_order_incomes", JSON.stringify(storedConfigs));
      } catch (e) {}

      const res = await donationApi.assignDriver({
        orderId,
        driverId: "",
        driverIncome: 0,
      });

      if (res.data.code === 0) {
        enqueueSnackbar("Đã gỡ gán tài xế thành công!", { variant: "success" });
        refresh();
        handleClose();
      } else {
        enqueueSnackbar(res.data.message || "Gỡ gán thất bại", { variant: "error" });
      }
    } catch (err: any) {
      console.error(err);
      enqueueSnackbar("Lỗi khi gỡ gán tài xế", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const estimated = calculateEstimatedEarnings();

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle style={{ fontWeight: "bold", borderBottom: "1px solid #f0f0f0" }}>
        Gán tài xế & Cấu hình tiền nhận đơn
      </DialogTitle>
      <DialogContent style={{ paddingTop: 20 }}>
        {/* Order Base Info */}
        {basePrice > 0 && (
          <Box
            sx={{
              p: 1.5,
              mb: 2.5,
              bgcolor: "#f8fafc",
              borderRadius: 1.5,
              border: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Mã đơn: <b>{order?.code || orderId}</b>
            </Typography>
            <Typography variant="body2" color="primary.main" fontWeight="bold">
              Giá cước đơn hàng: {basePrice.toLocaleString("vi-VN")} VNĐ
            </Typography>
          </Box>
        )}

        {/* Driver Selection */}
        <FormControl fullWidth style={{ marginBottom: 20 }}>
          <InputLabel>Chọn tài xế phụ trách</InputLabel>
          <Select
            value={selectedDriver}
            label="Chọn tài xế phụ trách"
            onChange={(e) => setSelectedDriver(e.target.value as string)}
          >
            {drivers.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name} ({d.phone})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Rate Type Selection */}
        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: "#334155" }}>
          Hình thức chi trả cho tài xế:
        </Typography>
        <RadioGroup
          row
          value={rateType}
          onChange={(e) => setRateType(e.target.value as "fixed" | "percentage")}
          style={{ marginBottom: 16 }}
        >
          <FormControlLabel
            value="fixed"
            control={<Radio color="primary" />}
            label="Giá cố định (VNĐ)"
          />
          <FormControlLabel
            value="percentage"
            control={<Radio color="primary" />}
            label="Phần trăm đơn hàng (%)"
          />
        </RadioGroup>

        {/* Input Value */}
        <TextField
          fullWidth
          label={rateType === "fixed" ? "Nhập số tiền tài xế nhận được" : "Nhập số phần trăm (%)"}
          placeholder={rateType === "fixed" ? "Ví dụ: 25000" : "Ví dụ: 70"}
          type="number"
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {rateType === "fixed" ? "VNĐ" : "%"}
              </InputAdornment>
            ),
          }}
          style={{ marginBottom: 16 }}
        />

        {/* Preview Calculated Amount */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: estimated > 0 ? "#f0fdf4" : "#f8fafc",
            borderRadius: 1.5,
            border: estimated > 0 ? "1px dashed #22c55e" : "1px dashed #94a3b8",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: estimated > 0 ? "#166534" : "#475569", fontWeight: 500 }}>
            {rateType === "fixed"
              ? "Tài xế sẽ nhận cố định:"
              : `Thực nhận (${customValue || 0}% của ${basePrice.toLocaleString("vi-VN")} VNĐ):`}
          </Typography>
          <Typography variant="h6" sx={{ color: estimated > 0 ? "#15803d" : "#334155", fontWeight: "bold" }}>
            {estimated.toLocaleString("vi-VN")} VNĐ
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between" }}>
        <div>
          {order?.driverId ? (
            <Button
              onClick={handleUnassign}
              color="error"
              variant="outlined"
              disabled={loading}
              style={{ textTransform: "none" }}
            >
              Gỡ gán tài xế
            </Button>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={handleClose} color="inherit">
            Hủy
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
            {loading ? "Đang lưu..." : "Xác nhận & Gán đơn"}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAssignDriver;

